import { createSlice } from '@reduxjs/toolkit';
import {
  request,
  generateCancelToken,
  cancelRequests,
  isCancel,
  ejectCancelInterceptor,
} from 'modules/Api/HttpClient';
import {
  USER_UNIT_ADMINS_ACTIVATE_URL,
  USER_UNIT_ADMINS_URL,
  SETTINGS_REMOVE_AVATAR_URL,
  USER_UNIT_ADMINS_CREATE_URL,
  USER_UNIT_ADMINS_UPDATE_URL,
  USER_UNIT_ADMINS_DELETE_URL,
  USER_UNIT_ADMIN_BY_ID_URL,
  LANGUAGES_URL,
  TIMEZONES_URL,
  GROUPS_URL,
  UNITS_BY_GROUP_URL,
} from 'modules/Api/Routes';
import { formDataFromObj } from 'modules/Api/RequestData';
import { defaultRequest } from 'modules/Utils/DefaultRequest';

let cancelToken;

const initialState = {
  loading: false,
  error: null,
  data: {
    page: 1,
    perPage: 10,
    groupId: '',
    unitId: '',
    search: '',
    sort: {
      sortType: null,
      sortBy: null,
    },
  },
  languages: {
    data: [],
    loading: false,
  },
  timezones: {
    data: [],
    loading: false,
  },
  groups: {
    data: [],
    loading: false,
  },
  units: {
    data: [],
    empty: false,
    loading: false,
  },
  isDeleting: false,
};

const unitAdminSlice = createSlice({
  name: 'unitAdmin',
  initialState,
  reducers: {
    cancelRequests: () => {
      cancelToken?.cancel();
      cancelRequests();
    },
    cleanState: () => ({ ...initialState }),
    /**
     * indicate that a request is started
     */
    requestUnitAdmin: (state) => {
      state.loading = true;
      state.error = null;
    },
    changePerPage: (state, action) => {
      state.data.perPage = action.payload;
      state.data.page = 1;
    },
    changeSort: (state, action) => {
      const newSort = action.payload;
      state.data.sort.sortType =
        state.data.sort.sortBy === newSort && state.data.sort.sortType === 'ASC'
          ? 'DESC'
          : 'ASC';
      state.data.sort.sortBy = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setIsDeleting: (state, action) => {
      state.isDeleting = action.payload;
    },
    requestLanguages: (state) => {
      state.languages = {
        ...state.languages,
        loading: true,
      };
    },
    requestTimezones: (state) => {
      state.timezones = {
        ...state.timezones,
        loading: true,
      };
    },
    receiveTimezonesList: (state, action) => {
      state.timezones = {
        ...state.timezones,
        loading: false,
        data: action.payload.content,
      };
    },
    receiveTimezonesError: (state, action) => {
      state.timezones = {
        ...state.timezones,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveLanguagesError: (state, action) => {
      state.languages = {
        ...state.languages,
        loading: false,
      };
      state.error = action.payload;
    },
    /**
     * receive a success response
     */
    receiveRequestSuccess: (state) => {
      state.lading = false;
    },
    /**
     * receive a success unitAdmin list response
     */
    receiveUnitAdminList: (state, action) => {
      state.loading = false;
      state.data = {
        ...state.data,
        unitAdmin: action.payload.users,
        total: action.payload.total_items,
      };
    },
    clearUnitAdminList: (state) => {
      state.loading = false;
      state.data = {
        page: 1,
        perPage: 10,
        groupId: '',
        unitId: '',
        sort: {
          sortType: null,
          sortBy: null,
        },
      };
    },
    /**
     * receive a success language list response
     */
    receiveLanguagesList: (state, action) => {
      state.languages = {
        ...state.languages,
        loading: false,
        data: action.payload.content,
      };
    },
    /**
     * receive an error response
     */
    receiveUnitAdminError: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    changeUnitAdminPage: (state, action) => {
      state.data.page = action.payload;
    },
    requestUnits: (state) => {
      state.units = {
        ...state.units,
        loading: true,
      };
    },
    receiveUnitsList: (state, action) => {
      state.units = {
        ...state.units,
        loading: false,
        empty: !action.payload.content.length,
        data: action.payload.content,
      };
    },
    receiveUnitsError: (state, action) => {
      state.units = {
        ...state.units,
        loading: false,
      };
      state.error = action.payload;
    },
    requestGroups: (state) => {
      state.groups = {
        ...state.groups,
        loading: true,
      };
    },
    receiveGroupsError: (state, action) => {
      state.groups = {
        ...state.groups,
        loading: false,
      };
      state.error = action.payload;
    },
    receiveGroupsList: (state, action) => {
      state.groups = {
        ...state.groups,
        loading: false,
        data: action.payload.content.groups,
      };
    },
    clearFormState: (state) => {
      state.units = {
        data: [],
        empty: false,
        loading: false,
      };
    },
    changeUnitAdminSearch: (state, action) => {
      state.data.groupId = action.payload.group_id;
      state.data.unitId = action.payload.unit_id;
      state.data.search = action.payload.search;
      state.data.page = 1;
    },
    changeUnitAdminFilters: (state, action) => {
      state.data.groupId = action.payload?.group_id || '';
      state.data.unitId = action.payload?.unit_id || '';
      state.data.page = isNaN(action?.payload?.page)
        ? 1
        : Number(action?.payload?.page);
      state.data.perPage = isNaN(action?.payload?.paginates_per)
        ? state.data.perPage
        : Number(action?.payload?.paginates_per);
      state.data.search = action?.payload.search || '';
    },
    clearUnitAdminsFilters: (state) => {
      state.data.groupId = null;
      state.data.unitId = null;
      state.data.search = '';
      state.data.page = 1;
      state.data.perPage = 10;
      state.data.sort.sortType = null;
      state.data.sort.sortBy = null;
    },
  },
});

const Actions = unitAdminSlice.actions;

const Selectors = {
  fetchListData: (state) => state.unitAdmin,
  unitAdminLoading: ({ unitAdmin: { loading } }) => ({ loading }),
};

const Async = {
  setActivated:
    ({ data: unitAdmin, field, activated, onSuccess, onError }) =>
    async () => {
      try {
        const data = new FormData();
        data.set('id', unitAdmin.id);
        data.set(field, activated);

        const response = await request({
          method: 'PUT',
          url: USER_UNIT_ADMINS_ACTIVATE_URL,
          data,
        });

        onSuccess(response);
      } catch (e) {
        onError(e);
      }
    },

  fetchUnitAdminList: () => async (dispatch, getState) => {
    const {
      unitAdmin: {
        data: {
          page,
          perPage,
          search,
          groupId,
          unitId,
          sort: { sortType, sortBy },
        },
      },
    } = getState();

    ejectCancelInterceptor();
    cancelToken?.cancel();
    cancelToken = generateCancelToken();

    let action;

    dispatch(Actions.requestUnitAdmin());

    try {
      const response = await request({
        cancelToken: cancelToken.token,
        method: 'GET',
        url: USER_UNIT_ADMINS_URL,
        params: {
          page,
          paginates_per: perPage,
          search,
          sort: sortType,
          sort_by: sortBy,
          group_id: groupId,
          unit_id: unitId,
        },
      });

      action = Actions.receiveUnitAdminList(response.data.content);
    } catch (e) {
      if (!isCancel(e)) {
        action = Actions.receiveUnitAdminError(e.message);
      }
    }

    action && dispatch(action);
  },

  removeAvatar:
    ({ id, onSuccess, onError }) =>
    async (dispatch) => {
      try {
        const data = formDataFromObj({ id });

        const response = await request({
          method: 'PUT',
          url: SETTINGS_REMOVE_AVATAR_URL,
          data,
        });
        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        onError(e);
      }
    },

  createUnitAdmin:
    ({ data, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestUnitAdmin());
      try {
        if (!data.avatar) delete data.avatar;
        const unitAdminData = formDataFromObj(data);

        const response = await request({
          method: 'POST',
          url: USER_UNIT_ADMINS_CREATE_URL,
          data: unitAdminData,
        });

        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.receiveUnitAdminError());
        onError(e);
      }
    },

  getUnitAdminById:
    ({ id, onSuccess, onError }) =>
    async () => {
      try {
        const response = await request({
          method: 'GET',
          url: `${USER_UNIT_ADMIN_BY_ID_URL}?id=${id}`,
        });

        onSuccess(response);
      } catch (e) {
        onError(e);
      }
    },

  deleteUnitAdmin:
    ({ id, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.setIsDeleting(true));

      try {
        const response = await request({
          method: 'DELETE',
          url: `${USER_UNIT_ADMINS_DELETE_URL}?id=${id}`,
        });

        onSuccess(response);
        dispatch(Actions.setIsDeleting(false));
      } catch (e) {
        onError(e);
        dispatch(Actions.setIsDeleting(false));
      }
    },

  updateUnitAdmin:
    ({ data, onSuccess, onError }) =>
    async (dispatch) => {
      dispatch(Actions.requestUnitAdmin());
      try {
        if (!(data.avatar instanceof File)) delete data.avatar;
        const unitAdminData = formDataFromObj(data);

        const response = await request({
          method: 'PUT',
          url: USER_UNIT_ADMINS_UPDATE_URL,
          data: unitAdminData,
        });

        dispatch(Actions.receiveRequestSuccess());
        onSuccess(response);
      } catch (e) {
        dispatch(Actions.receiveUnitAdminError());
        onError(e);
      }
    },

  fetchLanguagesList: () => async (dispatch) =>
    defaultRequest(
      dispatch,
      LANGUAGES_URL,
      'GET',
      Actions.requestLanguages,
      Actions.receiveLanguagesList,
      Actions.receiveLanguagesError
    ),

  fetchTimezonesList: () => async (dispatch) =>
    defaultRequest(
      dispatch,
      TIMEZONES_URL,
      'GET',
      Actions.requestTimezones,
      Actions.receiveTimezonesList,
      Actions.receiveTimezonesError
    ),

  fetchGroupsList: () => async (dispatch) =>
    defaultRequest(
      dispatch,
      `${GROUPS_URL}?page=1&paginates_per=1000`,
      'GET',
      Actions.requestGroups,
      Actions.receiveGroupsList,
      Actions.receiveGroupsError
    ),

  fetchUnitsList: (groupId) => async (dispatch) =>
    defaultRequest(
      dispatch,
      `${UNITS_BY_GROUP_URL}?group_id=${groupId}`,
      'GET',
      Actions.requestUnits,
      Actions.receiveUnitsList,
      Actions.receiveUnitsError
    ),
};

const { reducer } = unitAdminSlice;

export { reducer, Actions, Async, Selectors };
